import { Injectable } from '@angular/core';
import { SxHttpService } from './sxHttp.service';
import { Observable } from 'rxjs/Observable';
import { SkinProperty, SkinItem, SkinFileItem } from '../model/interfaces/skinItem.interface';
import { SkinItemType } from '../model/enum/skinItemType.enum';

export interface Sponsor {
  codSponsor: string;
  codInstalacionV5: string;
  codNegocio: string;
  alias: string;
  descripcion: string;
  skin: any;
  url: string;
}

export interface Skin {
  [key: string]: SkinProperty;
}

/**
 * Representa un PanelItemAssetVM en el Backend
 */
export interface PanelItemAssetVM {
  idAsset?: string;
  codigo: string;
  descripcion: string;
  tipo: string;
  urlGet: string;
  urlReset: string;
  assetConfigurado: boolean;
  esTipoTexto: boolean;
  esTipoImagen: boolean;
  esTipoFileManager: boolean;
}

/**
 * Representa un SponsorAssetText
 */
export interface SponsorAssetText extends SponsorAsset {
  assetContent: string;
}

/**
 * Representa un SponsorAssetFile
 */
export interface SponsorAssetFile extends SponsorAsset {
  fileName: string;
  fileExtension: string;
  assetContent?: string;
  assetContentType: string;
  assetSize: number;
  assetWidth?: string;
  assetHeight?: string;
  urlImage?: string;
}

/**
 * Representa un SponsorAsset
 */
export interface SponsorAsset {
  idAsset?: string;
  codAsset: string;
  codInstalacionV5: string;
  codNegocio: string;
  codSponsor: string;
  codProducto: string;
  descripcion: string;
}

@Injectable()
export class AssetsService {
  url = 'api/configuracionAsset';

  constructor(private sxHttp: SxHttpService) { }

  /**
   * Obtiene el Sponsor asociado al negocio
   *
   * @param codSistema
   * @param codNegocio
   * @param codSponsor
   */
  getSponsor(codOrganization:string, codSistema: string, codNegocio: string, codSponsor: string): Observable<Sponsor> {
    const url = `${this.url}/${codOrganization}/${codSistema}/${codNegocio}/${codSponsor}`;
    return this.sxHttp.get(url)
      .map(response => {
        const sponsor = response.json().data as Sponsor;
        const defaultSkin = new SkinItems();
        if (!sponsor.skin) {
          sponsor.skin = defaultSkin;
        } else {
          sponsor.skin = JSON.parse(sponsor.skin);
          Object.keys(defaultSkin).forEach(key => {
            if (!sponsor.skin[key]) {
              sponsor.skin[key] = defaultSkin[key];
            }
          });
        }



        return sponsor;
      });
  }

  /**
   * Guarda un sponsor
   * @param sponsor
   */
  guardarSponsor(sponsor: Sponsor, originalAlias: String) {
    const url = `${this.url}/${originalAlias}/${sponsor}`;
    return this.sxHttp.post(url, sponsor, originalAlias)
      .toPromise();
  }

  /**
   * Obtiene el listado de Assets asociados a un Sponsor
   *
   * @param codSistema
   * @param codNegocio
   * @param codSponsor
   */
  getBySponsor(codOrganizacion:string, codSistema: string, codNegocio: string, codSponsor: string): Promise<PanelItemAssetVM[]> {
    const url = `${this.url}/BySponsor/${codOrganizacion}/${codSistema}/${codNegocio}/${codSponsor}`;
    return this.sxHttp.get(url)
      .toPromise()
      .then(response => response.json().data as PanelItemAssetVM[]);
  }

  /**
   * Obtiene el listado de Assets asociados a un Producto
   *
   * @param codSistema
   * @param codNegocio
   * @param codSponsor
   * @param codProducto
   */
  getByProducto(codOrganizacion:string, codSistema: string, codNegocio: string, codSponsor: string, codProducto: string): Promise<PanelItemAssetVM[]> {
    const url = `${this.url}/ByProducto/${codOrganizacion}/${codSistema}/${codNegocio}/${codSponsor}/${codProducto}`;
    return this.sxHttp.get(url)
      .toPromise()
      .then(response => response.json().data as PanelItemAssetVM[]);
  }

  /**
   * Obtiene el listado de Assets por tipo
   *
   * @param tipoAsset
   * @param codSistema
   * @param codSponsor
   * @param codNegocio
   */
  getByTipo(tipo: string, codSistema: string, codSponsor: string, codNegocio: string, codProducto: string): Observable<SponsorAssetFile[]> {
    let url = `${this.url}/ByTipo/${codSistema}/${codSponsor}/${codNegocio}`;

    if (codProducto) {
      url += `/${codProducto}`;
    }

    const params = {
      tipoAsset: tipo,
    };

    return this.sxHttp.get(url, params)
      .map(response => response.json().data as SponsorAssetFile[]);
  }

  /**
   * Elimina el asset que tenga el assetId dado
   *
   * @param assetId El id del asset a eliminar
   */
  eliminar(assetId): Promise<SponsorAsset> {
    return this.sxHttp.delete(`${this.url}/${assetId}`)
      .toPromise()
      .then(res => res.json().data as SponsorAsset);
  }

  /**
   * Obtiene un SponsorAssetText por id
   *
   * @param codSistema
   * @param codNegocio
   * @param codSponsor
   * @param codAsset
   */
  getAssetTextById(codSistema: string, codNegocio: string, codSponsor: string, codAsset: string): Promise<SponsorAssetText> {
    const url = `${this.url}/${codSistema}/${codNegocio}/${codSponsor}/${codAsset}`;
    return this.sxHttp.get(url)
      .toPromise()
      .then(response => response.json().data as SponsorAssetText);
  }

  crearAssetText(asset: SponsorAssetText) {
    const url = `${this.url}/PostAssetText`;
    return this.sxHttp.post(url, asset)
      .toPromise();
  }

  editarAssetText(idAsset: string, asset: SponsorAssetText) {
    const url = `${this.url}/PutAssetText/${idAsset}`;
    return this.sxHttp.put(url, asset)
      .toPromise();
  }

  /**
   * Obtiene un SponsorAssetFile por id
   *
   * @param codSistema
   * @param codNegocio
   * @param codSponsor
   * @param codAsset
   */
  getAssetFileById(codSistema: string, codNegocio: string, codSponsor: string, codAsset: string): Promise<SponsorAssetFile> {
    const url = `${this.url}/${codSistema}/${codNegocio}/${codSponsor}/${codAsset}`;
    return this.sxHttp.get(url)
      .toPromise()
      .then(response => response.json().data as SponsorAssetFile);
  }

  crearAssetFile(asset: SponsorAssetFile) {
    const url = `${this.url}/PostAssetFile`;
    return this.sxHttp.post(url, asset)
      .toPromise();
  }

  editarAssetFile(idAsset: string, asset: SponsorAssetFile) {
    const url = `${this.url}/PutAssetFile/${idAsset}`;
    return this.sxHttp.put(url, asset)
      .toPromise();
  }
}

class SkinItems {
  btn_primary_color: SkinItem = { label: 'BTN_PRIMARY_COLOR', type: SkinItemType.color, value: '#f6b221', default: '#f6b221' };
  btn_secondary_color: SkinItem = { label: 'BTN_SECONDARY_COLOR', type: SkinItemType.color, value: '#db9809', default: '#db9809' };
  highlight: SkinItem = { label: 'HIGHLIGHT_COLOR', type: SkinItemType.color, value: '#e8b850', default: '#e8b850' };
  breadcrumb_bg_color: SkinItem = { label: 'BREADCRUMN_BACK_COLOR', type: SkinItemType.color, value: '#e9e9e9', default: '#e9e9e9' };
  breadcrumb_color: SkinItem = { label: 'BREADCRUMN_FONT_COLOR', type: SkinItemType.color, value: '#636c72', default: '#636c72' };
  bg_navbar_color: SkinItem = { label: 'NAVBAR_BACK_COLOR', type: SkinItemType.color, value: '#333333', default: '#333333' };
  tab_title_b2b: SkinItem = { label: 'TAB_TITLE_B2B', type: SkinItemType.text, value: '', default: '#{SITE_TITLE}#' };
  tab_title_b2c: SkinItem = { label: 'TAB_TITLE_B2C', type: SkinItemType.text, value: '', default: '#{SITE_TITLE}#' };
  login_text_paragraph: SkinItem = { label: 'B2B_TEXT', type: SkinItemType.textarea, value: '', default: '' };
  login_logo: SkinFileItem = { label: 'B2B_LOGO', type: SkinItemType.file, value: null, path: 'Sponsor/Logo' };
  tab_favicon_b2b: SkinFileItem = { label: 'TAB_FAVICON_B2B', type: SkinItemType.file, value: null, path: 'Sponsor/Favicon/B2B' };
  tab_favicon_b2c: SkinFileItem = { label: 'TAB_FAVICON_B2C', type: SkinItemType.file, value: null, path: 'Sponsor/Favicon/B2C' };
  custom_css: SkinFileItem = { label: 'CUSTOM_CSS', type: SkinItemType.file, value: null, path: 'Sponsor/CSS' };
  custom_grid_theme: SkinFileItem = { label: 'CUSTOM_GRID_THEME', type: SkinItemType.file, value: null, path: 'Sponsor/CustomGrid' };
  captcha: SkinItem = { label: 'CAPTCHA', type: SkinItemType.boolean, value: 'false', default: 'false' };
  multifactor: SkinItem = { label: 'MULTIFACTOR_AUTH', type: SkinItemType.boolean, value: 'false', default: 'false'};
  active_directory: SkinItem = { label: 'ACTIVE_D', type: SkinItemType.boolean, value: 'false', default: 'false', description: 'ACTIVE_D_DESC' };
  only_active_directory: SkinItem = { label: 'ONLY_ACTIVE_D', type: SkinItemType.boolean, value: 'false', default: 'false', dependency:'active_directory' };
}



// WEBPACK FOOTER //
// D:/a/1/s/BackOffice/SOC/SxSoc.ClientWeb/src/app/infrastructure/services/assets.service.ts